<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import {
    mdiViewDashboard, mdiPackageVariant, mdiServerNetwork,
    mdiCogOutline, mdiGraphOutline, mdiChevronDown, mdiChevronRight,
    mdiServer, mdiMapMarker
} from '@mdi/js';
import BrandLogo from './BrandLogo.vue';
import MdiIcon from './MdiIcon.vue';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { useLocaleStore } from '@/stores/locale';
import { useModulesStore } from '@/stores/modules';
import { resolveMdiIcon } from './mdi-icons';
import { fleetApi, type FleetNode } from '@/api/fleet';
import type { NavEntry, ModuleManifest } from './types';
import { translateLabel } from '@/i18n';

/** Translate label if it's an i18n key, otherwise return as-is.
 *  Reactive: re-evaluates when locale or messages change. */
function tl(label: string, fallback?: string): string {
    return translateLabel(label, fallback);
}

const auth = useAuthStore();
const theme = useThemeStore();
const locale = useLocaleStore();
const modules = useModulesStore();
const route = useRoute();
const router = useRouter();

/** Встроенные системные пункты (не из манифестов). */
const systemItems = computed<NavEntry[]>(() => [
    { route: '/dashboard', label: tl('sidenav.dashboard'), icon: mdiViewDashboard, order: 0 },
    { route: '/modules', label: tl('sidenav.modulesCatalog'), icon: mdiPackageVariant, order: 10 },
    { route: '/nodes', label: tl('sidenav.nodes'), icon: mdiServerNetwork, order: 20 },
    { route: '/fleet/graph', label: tl('sidenav.routing'), icon: mdiGraphOutline, order: 90 },
]);

// --- Node selector ---

const NODE_STORAGE_KEY = 'cavicode.selectedNodeId';
const nodes = ref<FleetNode[]>([]);
const selectedNodeId = ref<string | null>(null);
const nodeDropdownOpen = ref(false);

async function loadNodes() {
    try {
        nodes.value = await fleetApi.listNodes();
        const queryNode = typeof route.query?.node === 'string' ? route.query.node : null;
        let saved: string | null = null;
        try {
            saved = localStorage.getItem(NODE_STORAGE_KEY);
        } catch {
            // Storage может быть запрещён политикой браузера; URL остаётся источником состояния.
        }

        const restored = [queryNode, saved, selectedNodeId.value]
            .find(id => id && nodes.value.some(node => node.id === id));
        const fallback = restored ?? nodes.value[0]?.id ?? null;
        if (fallback) {
            selectedNodeId.value = fallback;
            persistNode(fallback);
            if (queryNode !== fallback) void replaceNodeQuery(fallback);
        }
    } catch {
        // Не стираем последнюю выбранную ноду при временной ошибке Fleet API.
    }
}

loadNodes();

const selectedNodeLabel = computed(() => {
    if (!selectedNodeId.value) return 'Выберите ноду';
    const node = nodes.value.find(n => n.id === selectedNodeId.value);
    return node?.name ?? 'Выберите ноду';
});

function selectNode(nodeId: string) {
    selectedNodeId.value = nodeId;
    persistNode(nodeId);
    void replaceNodeQuery(nodeId);
    nodeDropdownOpen.value = false;
}

function persistNode(nodeId: string) {
    try {
        localStorage.setItem(NODE_STORAGE_KEY, nodeId);
    } catch {
        // Выбранная нода всё равно сохраняется в URL.
    }
}

function replaceNodeQuery(nodeId: string) {
    return router.replace({ query: { ...(route.query ?? {}), node: nodeId } });
}

function withSelectedNode(path: string) {
    return {
        path,
        query: selectedNodeId.value ? { node: selectedNodeId.value } : undefined
    };
}

watch(
    () => route.query?.node,
    (value) => {
        const nodeId = typeof value === 'string' ? value : null;
        if (nodeId && nodes.value.some(node => node.id === nodeId)) {
            selectedNodeId.value = nodeId;
            persistNode(nodeId);
        }
    }
);

// --- Collapsible module groups ---

/** Группировка nav entries по модулям. */
interface ModuleGroup {
    manifest: ModuleManifest;
    entries: NavEntry[];
}

const moduleGroups = computed<ModuleGroup[]>(() => {
    const groups: ModuleGroup[] = [];
    for (const manifest of modules.manifests) {
        const entries: NavEntry[] = [];
        for (const entry of manifest.navEntries) {
            if (entry.adminOnly && !auth.canAdmin) continue;
            entries.push({
                route: entry.route,
                label: entry.label,
                icon: resolveMdiIcon(entry.icon),
                order: entry.order ?? 100,
            });
        }
        if (entries.length === 0) continue;
        entries.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
        groups.push({ manifest, entries });
    }
    return groups;
});

/** Модули, установленные на выбранной ноде (по moduleId). */
const installedModuleIds = computed<Set<string>>(() => {
    if (!selectedNodeId.value) return new Set();
    const node = nodes.value.find(n => n.id === selectedNodeId.value);
    if (!node) return new Set();
    return new Set(node.modules.map(m => m.moduleId));
});

/** Модульные группы, отфильтрованные по выбранной ноде. */
const visibleModuleGroups = computed<ModuleGroup[]>(() => {
    return moduleGroups.value.filter(g => installedModuleIds.value.has(g.manifest.id));
});

/** Развёрнутые секции (по moduleId). По умолчанию все развёрнуты. */
const expandedSections = ref<Set<string>>(new Set());

function toggleSection(moduleId: string) {
    if (expandedSections.value.has(moduleId)) {
        expandedSections.value.delete(moduleId);
    } else {
        expandedSections.value.add(moduleId);
    }
}

function isExpanded(moduleId: string): boolean {
    return expandedSections.value.has(moduleId);
}

/** Автоматически раскрываем секцию активного модуля. */
watch([() => route.path, moduleGroups], ([path]) => {
    for (const group of moduleGroups.value) {
        const isActive = group.entries.some(e => path.startsWith(e.route));
        if (isActive && !isExpanded(group.manifest.id)) {
            expandedSections.value.add(group.manifest.id);
        }
    }
}, { immediate: true });
</script>

<template>
    <aside class="side-nav">
        <div class="side-nav__brand">
            <RouterLink :to="withSelectedNode('/dashboard')">
                <BrandLogo />
            </RouterLink>
        </div>

        <!-- Системные разделы (не зависят от ноды) -->
        <div class="nav-group-label">{{ tl('sidenav.system') }}</div>
        <nav class="side-nav__section" :aria-label="tl('sidenav.system')">
            <RouterLink
                v-for="i in systemItems"
                :key="i.route"
                :to="withSelectedNode(i.route)"
                class="nav-item"
                active-class="nav-item--active"
            >
                <MdiIcon :path="i.icon" :size="20" />
                <span>{{ i.label }}</span>
            </RouterLink>
        </nav>

        <!-- Node selector (под системой) -->
        <div class="node-selector">
            <div class="nav-group-label">{{ tl('sidenav.node') }}</div>
            <button
                class="node-selector__button"
                :class="{ 'node-selector__button--active': nodeDropdownOpen }"
                @click="nodeDropdownOpen = !nodeDropdownOpen"
            >
                <MdiIcon :path="mdiMapMarker" :size="16" />
                <span class="node-selector__label">{{ selectedNodeLabel }}</span>
                <MdiIcon :path="nodeDropdownOpen ? mdiChevronDown : mdiChevronRight" :size="16" />
            </button>
            <div v-if="nodeDropdownOpen" class="node-selector__dropdown">
                <button
                    v-for="node in nodes"
                    :key="node.id"
                    class="node-selector__option"
                    :class="{ 'node-selector__option--selected': selectedNodeId === node.id }"
                    @click="selectNode(node.id)"
                >
                    <MdiIcon :path="mdiServer" :size="16" />
                    <span>{{ node.name }}</span>
                    <span class="node-selector__status" :class="`node-selector__status--${node.status.toLowerCase()}`" />
                </button>
            </div>
        </div>

        <!-- Модульные секции (collapsible, фильтруются по ноде) -->
        <div class="nav-group-label">{{ tl('sidenav.modules') }}</div>
        <nav class="side-nav__list" :aria-label="tl('sidenav.modules')">
            <template v-for="group in visibleModuleGroups" :key="group.manifest.id">
                <div class="module-group">
                    <button
                        class="module-group__header"
                        @click="toggleSection(group.manifest.id)"
                    >
                        <MdiIcon
                            :path="isExpanded(group.manifest.id) ? mdiChevronDown : mdiChevronRight"
                            :size="16"
                        />
                        <MdiIcon :path="resolveMdiIcon(group.manifest.icon)" :size="16" />
                        <span>{{ tl(group.manifest.displayName) }}</span>
                    </button>
                    <div v-show="isExpanded(group.manifest.id)" class="module-group__items">
                        <RouterLink
                            v-for="entry in group.entries"
                            :key="entry.route"
                            :to="withSelectedNode(entry.route)"
                            class="nav-item nav-item--child"
                            active-class="nav-item--active"
                        >
                            <MdiIcon :path="entry.icon" :size="18" />
                            <span>{{ tl(entry.label, entry.label.endsWith('.title') ? group.manifest.displayName : undefined) }}</span>
                        </RouterLink>
                    </div>
                </div>
            </template>
        </nav>

        <div class="side-nav__footer">
            <RouterLink :to="withSelectedNode('/settings')" class="nav-item side-nav__settings-link">
                <MdiIcon :path="mdiCogOutline" :size="20" />
                <span>{{ tl('sidenav.settings') }}</span>
            </RouterLink>
            <div class="side-nav__footer-info">
                <span class="side-nav__swatch" aria-hidden="true" />
                <span>{{ tl(`themes.${theme.mode}`) }}</span>
            </div>
        </div>
    </aside>
</template>

<style scoped>
.side-nav {
    position: relative;
    grid-area: nav;
    z-index: 2;
    background: var(--shell-nav);
    border-right: 1px solid var(--shell-divider);
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    overflow-y: auto;
}
.side-nav__brand {
    padding: 4px 8px 16px;
    border-bottom: 1px solid var(--shell-divider);
    margin-bottom: 12px;
}
.side-nav__brand a {
    text-decoration: none;
    color: var(--ink);
}
.side-nav__section {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 8px;
}
.side-nav__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

/* Section labels */
.nav-group-label {
    padding: 8px 8px 4px;
    color: var(--ink-mute);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

/* Node selector */
.node-selector {
    position: relative;
    margin-bottom: 8px;
}
.node-selector__button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid var(--shell-divider);
    background: var(--shell-bg);
    color: var(--ink-soft);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background var(--transition-fast), border-color var(--transition-fast);
}
.node-selector__button:hover {
    background: var(--hover-soft);
    border-color: var(--accent);
}
.node-selector__button--active {
    border-color: var(--accent);
    background: var(--accent-tint);
}
.node-selector__label {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.node-selector__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    border-radius: 6px;
    border: 1px solid var(--shell-divider);
    background: var(--shell-bg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10;
    overflow: hidden;
}
.node-selector__option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: transparent;
    color: var(--ink-soft);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background var(--transition-fast);
}
.node-selector__option:hover {
    background: var(--hover-soft);
}
.node-selector__option--selected {
    color: var(--accent);
    font-weight: 600;
}
.node-selector__status {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    margin-left: auto;
    flex-shrink: 0;
}
.node-selector__status--online { background: #22c55e; }
.node-selector__status--offline { background: #ef4444; }
.node-selector__status--degraded { background: #f59e0b; }

/* Module groups with outline borders */
.module-group {
    border: 1px solid var(--shell-divider);
    border-radius: 8px;
    overflow: hidden;
}
.module-group__header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border: none;
    background: var(--shell-bg);
    color: var(--ink-soft);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background var(--transition-fast), color var(--transition-fast);
    width: 100%;
}
.module-group__header:hover {
    background: var(--hover-soft);
    color: var(--ink);
}
.module-group__items {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 4px 6px 6px;
    border-top: 1px solid var(--shell-divider);
}

/* Nav items */
.nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    color: var(--ink-soft);
    font-size: 13px;
    font-weight: 600;
    transition: background var(--transition-fast), color var(--transition-fast), opacity var(--transition-fast);
    text-decoration: none;
}
.nav-item--child {
    padding: 7px 12px;
    font-size: 12px;
    font-weight: 500;
}
.nav-item:hover {
    background: var(--hover-soft);
    color: var(--ink);
}
.nav-item--active {
    background: var(--accent-tint);
    color: var(--accent);
}
.nav-item--active:hover {
    background: var(--accent-tint);
    opacity: 0.9;
}

.side-nav__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 0 0;
    border-top: 1px solid var(--shell-divider);
    margin-top: 12px;
}
.side-nav__settings-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    color: var(--ink-soft);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: background var(--transition-fast), color var(--transition-fast);
}
.side-nav__settings-link:hover {
    background: var(--hover-soft);
    color: var(--ink);
}
.side-nav__footer-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    color: var(--ink-mute);
    font-size: 11px;
    line-height: 1.4;
}
.side-nav__swatch {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: var(--accent-gradient);
    flex-shrink: 0;
}

@media (max-width: 900px) {
    .side-nav {
        display: none;
        border-right: 0;
        border-bottom: 1px solid var(--shell-divider);
        max-height: 60dvh;
    }
    .side-nav--open { display: flex; }
    .side-nav__brand { display: none; }
    .side-nav__list { flex: none; }
    .nav-item, .module-group__header, .node-selector__button, .node-selector__option { min-height: 44px; }
}
</style>
